import React, { useEffect, useMemo, useState, useCallback, useContext } from "react";
import { format, addDays, startOfWeek, isBefore, isSameDay } from "date-fns";
import { Container, Card, Button, Form, Row, Col, Alert, Badge, Spinner, Modal } from 'react-bootstrap';
import PageHeader from 'components/common/PageHeader';
import { fetchUserHistory, createRequest, updateRequestStatus, fetchAllUsers } from 'services/charging-space-booking/chargingSpaceBookingService';
import { getItemFromStore } from 'helpers/utils';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import AppContext from 'context/Context';
import { getBays } from "services/charging-space-booking/chargingSpaceBookingService";
import SpecificDaysPicker from './SpecificDaysPicker'
import {STATUS, SITES, SLOTS, SLOT_TYPES, MAX_WEKKLY_REQUEST,WEEK_AHEAD_COUNT } from './Const'


function getWorkweek(anchor) {
    const monday = startOfWeek(anchor, { weekStartsOn: 1 });
    return [0, 1, 2, 3, 4].map((d) => addDays(monday, d));
}

const ConfirmModal = ({ show, handleClose, handleConfirm, title, body }) => (
    <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton className="border-bottom">
            <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-4">
            <div className="d-flex align-items-center">
                <i className="bi bi-exclamation-triangle-fill text-warning me-3" style={{ fontSize: '1.5rem' }}></i>
                <div>{body}</div>
            </div>
        </Modal.Body>
        <Modal.Footer className="border-0">
            <Button variant="outline-secondary" onClick={handleClose}>
                No
            </Button>
            <Button variant="primary" onClick={handleConfirm}>
                Yes
            </Button>
        </Modal.Footer>
    </Modal>
);

ConfirmModal.propTypes = {
    show: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    handleConfirm: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired,
};

export default function EVChargingBookingPage() {
    const [storeUser, setStoreUser] = useState({});
    useEffect(() => {
    const interval = setInterval(() => {
        const user = getItemFromStore('user', {}, sessionStorage);
        if (user?.userName) {
        setStoreUser(user);
        clearInterval(interval);
        }
    }, 100);

    return () => clearInterval(interval);
    }, []);

    const {
        config: { isFluid, isNavbarVerticalCollapsed },
        setConfig
    } = useContext(AppContext);
    const [kanbanIsFluid] = useState(isFluid);
    const [kanbanIsNavbarVerticalCollapsed] = useState(isNavbarVerticalCollapsed);

    useEffect(() => {
        setConfig('isFluid', true);
        setConfig('isNavbarVerticalCollapsed', true);

        return () => {
        setConfig('isFluid', kanbanIsFluid);
        setConfig('isNavbarVerticalCollapsed', kanbanIsNavbarVerticalCollapsed);
        };
    }, []);

    const [anchor, setAnchor] = useState(() =>
        startOfWeek(new Date(), { weekStartsOn: 1 })
    );
   
    const todayWeekStart = useMemo(
        () => startOfWeek(new Date(), { weekStartsOn: 1 }),
        []
    );
 
    const limitWeekStart = addDays(todayWeekStart, WEEK_AHEAD_COUNT * 7);

    const {today,currentWeekStart,nextWeekStart } = useMemo(() => {
        const today = new Date(new Date().setHours(0, 0, 0, 0));
        const currentWeekStart = startOfWeek(anchor, { weekStartsOn: 1 });
        const nextWeekStart = addDays(currentWeekStart, 6);
        return { today, currentWeekStart, nextWeekStart };
    }, [anchor]);

    const canGoBack = isBefore(todayWeekStart, anchor);
    const canGoForward = isBefore(addDays(anchor, 7), limitWeekStart);

    const [site, setSite] = useState(SITES[0].id);
    const [evUser, setEvUser] = useState({});
    const [slotType, setSlotType] = useState("FULL");
    const [loading, setLoading] = useState(false);
    const [history, setHistory] = useState([]);
    const [userHistory, setUserHistory] = useState([]);
    const [isActive, setIsActive] = useState(true);
    const [userSite, setUserSite] = useState(null);
    const [userFound, setUserFound] = useState(true);
    const [siteLoading, setSiteLoading] = useState(false);

    const days = useMemo(() => getWorkweek(anchor), [anchor]);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelRequestId, setCancelRequestId] = useState(null);

    const [showReleaseModal, setShowReleaseModal] = useState(false);
    const [releaseRequestId, setReleaseRequestId] = useState(null);

    
    const isPastDay = (date) => isBefore(date, today) && !isSameDay(date, today);

    const displayedDays = useMemo(() => {
        const isCurrentWeekAnchor = isSameDay(anchor, currentWeekStart);

        if (isCurrentWeekAnchor) {
            return days.filter(d => !isPastDay(d, today)); 
        }
        
        return days; 
    }, [days, anchor, currentWeekStart, today, isPastDay]);

    const canUserRequest = useMemo(() => {
        return !loading && userFound && isActive && !siteLoading && userSite && userSite.trim() !== '';
    }, [loading, userFound, isActive, siteLoading, userSite]);

    const weeklyRequestCount = useMemo(() => {
        const countMap = new Map();
        userHistory.filter(req => 
            (req.status === 'PENDING' || req.status === 'APPROVED') 
            && req.requestedBy === storeUser.userName
        ).forEach(req => {
            const weekStart = startOfWeek(new Date(req.bookingDate), { weekStartsOn: 1 });
            const weekKey = format(weekStart, 'yyyy-MM-dd');
            countMap.set(weekKey, (countMap.get(weekKey) || 0) + 1);
        });
        return countMap;
    }, [userHistory]);

    const hasReachedWeeklyLimit = useCallback((date) => {
        if (!storeUser.userName) return true; 

        const weekStart = startOfWeek(date, { weekStartsOn: 1 });
        const weekKey = format(weekStart, 'yyyy-MM-dd');
        
        return (weeklyRequestCount.get(weekKey) || 0) >= MAX_WEKKLY_REQUEST;
    }, [weeklyRequestCount]);

    const [bays,setBays] = useState([]) 
    useEffect(() => {
         getBays().then(x=> {
             setBays(x.data.filter(x => x.siteName == site))
         })
    }, [site]); 

   const getRemainingSlotCount = useCallback((date) => {
    const dateKey = format(date, 'yyyy-MM-dd');

    const bookingsInDay = history.filter(
        x => format(x.bookingDate, 'yyyy-MM-dd') === dateKey
           && x.status === STATUS.APPROVED
    );

    const availableBays = bays.filter(bay => {

        const bookingsOfBay = bookingsInDay.filter(
            b => b.bayNo === bay.bayNo
        );

        if (bookingsOfBay.length === 0) return true;

        if (slotType === SLOTS.FULL) {
            return false; // False if AM or PM booked
        }

        if (slotType === SLOTS.AM) {
            return !bookingsOfBay.some(
                b => b.slotType === SLOTS.FULL || b.slotType === SLOTS.AM
            );
        }

        // PM
        return !bookingsOfBay.some(
            b => b.slotType === SLOTS.FULL || b.slotType === SLOTS.PM
        );

    });
    console.log(availableBays, dateKey)
    return availableBays.length;

}, [history, bays, slotType]);

    const loadUserSite = async () => {
        if (!storeUser.userName) return;
        setSiteLoading(true);
        try {
            const allUsers = await fetchAllUsers();
            
            const currentUserData = allUsers.find(
                (u) => u.userId === storeUser.userId
            );

            if (!currentUserData) {
                setUserFound(false);
                setIsActive(false);
                setUserSite('');
                setSite(SITES[0].id);
            } else {
                setEvUser(currentUserData)
                setUserFound(true);
                
                const determinedIsActive = currentUserData.isActive === false ? false : true;
                const determinedSite = currentUserData.site ? currentUserData.site.trim() : '';

                setIsActive(determinedIsActive);

                if (determinedSite) {
                    const isValidSite = SITES.some(s => s.id === determinedSite);
                    if (isValidSite) {
                        setUserSite(determinedSite);
                        setSite(determinedSite);
                    } else {
                        toast.warn(`Administrator-set site "${determinedSite}" is invalid. Defaulting to ${SITES[0].name}.`);
                        setUserSite(SITES[0].id);
                        setSite(SITES[0].id);
                    }
                } else {
                    setUserSite('');
                    setSite(SITES[0].id);
                }
            }
        } catch (err) {
            toast.error(`Failed to load user settings: ${err.message}`);
            setUserFound(false);
            setIsActive(false);
            setUserSite('');
            setSite(SITES[0].id);
        } finally {
            setSiteLoading(false);
        }
    };

    useEffect(() => {
        loadUserSite();
    }, [storeUser?.userName]);

    const loadHistory = useCallback(async () => {
        setLoading(true);
        try {
            const dataUser = await fetchUserHistory(storeUser.userName, currentWeekStart, nextWeekStart);
            setUserHistory(dataUser.filter(req => req.status === STATUS.APPROVED && req.site == userSite));
            const data = await fetchUserHistory('', currentWeekStart, nextWeekStart);
            setHistory(data.filter(req => req.status === STATUS.APPROVED && req.site == userSite));
        } catch (err) {
            toast.error(err.message);
            setUserHistory([]);
            setHistory([]);
        } finally {
            setLoading(false);
        }
    }, [storeUser?.userName, currentWeekStart, nextWeekStart, userSite]);

    useEffect(() => {
        if (!siteLoading) {
            const hasValidSite = storeUser.userName && userSite && userSite.trim() !== '';
            
            if (hasValidSite) {
                loadHistory(); 
            } else {
                setUserHistory([]); 
            }
        }
    }, [storeUser?.userName, userSite, siteLoading, loadHistory]);

    async function submitRequests(dateList, forcedSlotType = null) {
        setLoading(true);

        const payloadArray = [];
        let conflictDetected = false;

        for (const date of dateList) {
            const dateKey = format(date, 'yyyy-MM-dd');
            const remainingSlot = getRemainingSlotCount(date);
            let slotToRequest = forcedSlotType || slotType;

            if (hasReachedWeeklyLimit(date)) {
                toast.warn(`Request for ${format(date, 'EEE d MMM')} is skipped. You have already reached the limit of ${MAX_WEKKLY_REQUEST} requests this week.`);
                conflictDetected = true; 
                continue;
            }
            
            if (remainingSlot === 0) {
                conflictDetected = true;
                continue;
            } 
            
            payloadArray.push({
                requestedBy: storeUser.userName,
                site: site,
                slotType: slotToRequest,
                bookingDate: dateKey
            });
        }
        
        if (payloadArray.length === 0) {
                toast.error(conflictDetected 
                ? "All selected days already have conflicting requests (FULL or both AM/PM)."
                : "No valid requests to submit."
            );
                setLoading(false);
                return;
        }

        try {
            const response = await createRequest(payloadArray);
            console.log(response)
            if (response.message && response.message.toUpperCase().includes("REJECTED")) {
                toast.error(response.message);
            }
            else {
                 toast.success(response.message || `Submitted ${payloadArray.length} request(s) successfully.`);
            }
           
            await loadHistory();
        } catch (err) {
            toast.error(err.message || "Could not submit your request. Please try again.");
        } finally {
            setLoading(false);
        }
    }

    async function submitOneDay(date) {
        if (!canUserRequest) {
            toast.error("You are not allowed to submit requests. Please check your user status.");
            return;
        }

        if (hasReachedWeeklyLimit(date)) {
            toast.error(`Request for ${format(date, 'EEE d MMM')} failed. You have already reached the limit of  ${MAX_WEKKLY_REQUEST} requests this week.`);
            return;
        }

        const remainingSlot = getRemainingSlotCount(date);
        
        if (remainingSlot === 0) {
            toast.error(`No remaining slot for ${format(date, 'EEE d MMM')}.`);
            return;
        }

        await submitRequests([date]);
    }

    const handleOpenCancelModal = (requestId) => {
        setCancelRequestId(requestId);
        setShowCancelModal(true);
    };

    const handleCancelConfirm = async () => {
        setShowCancelModal(false);
        setLoading(true);
        
        try {
            const response = await updateRequestStatus(cancelRequestId, 'CANCELLED', storeUser.userName);
            toast.success(response.message || "Your request has been successfully cancelled.");
        } catch (err) {
            toast.error(err.message || "Failed to cancel request.");
        } finally {
            await loadHistory();
            setLoading(false);
        }
    };

    const handleOpenReleaseModal = (requestId) => {
        setReleaseRequestId(requestId);
        setShowReleaseModal(true);
    };

    const handleReleaseConfirm = async () => {
        setShowReleaseModal(false);
        setLoading(true);
        
        try {
            const response = await updateRequestStatus(releaseRequestId, 'RELEASED', storeUser.userName);
            toast.success(response.message || "Released");
        } catch (err) {
            toast.error(err.message || "Release failed.");
        } finally {
            await loadHistory();
            setLoading(false);
        }
    };

    function renderStatusBadge(status) {
        let variant = 'secondary';
        let text = status;
        if (status === 'PENDING') {
            variant = 'warning';
            text = 'Pending Review';
        } else if (status === 'APPROVED') {
            variant = 'success';
            text = 'Approved';
        } else if (status === 'REJECTED') {
            variant = 'danger';
            text = 'Rejected';
        } else if (status === 'CANCELLED') {
            variant = 'secondary';
            text = 'Cancelled';
        } else if (status === 'RELEASED') {
            variant = 'info';
            text = 'Released';
        }
        return <Badge bg={variant}>{text}</Badge>;
    }

    function renderAvailabilityBadge(date, isDateRequested) {
        const disabled = isPastDay(date);
        const remainingSlot = getRemainingSlotCount(date);
        
        if (isDateRequested) {
            return <Badge bg="secondary">Requested</Badge>;
        }

        if (disabled) {
            return <Badge bg="secondary">Request unavailable</Badge>;
        }
        
        if (remainingSlot === 0) {
            return <Badge bg="secondary">Fully Booked</Badge>;
        }

        let label = 'Request available';
        // if (remainingSlot > 0 && remainingSlot != bays.length) {
        //         label = `Remains: ${remainingSlot}`; 
        // }
        return <Badge bg="success">{label}</Badge>; 
    }

    const isSiteLocked = !siteLoading && userSite && userSite.trim() !== '' && userSite === site;
    const currentSiteName = SITES.find(s => s.id === site)?.name || site;

    const userAlert = useMemo(() => {
        if (!siteLoading) {
            if (!userFound) {
                return {
                    variant: "danger",
                    message: "You have no setup yet. Please contact administrator for setup.",
                    show: true
                };
            }
            if (!isActive) {
                return {
                    variant: "danger",
                    message: "You are **inactive**. Please contact administrator for setup.",
                    show: true
                };
            }
            if (!userSite) { 
                return {
                    variant: "danger",
                    message: "You have no site setup yet. Please contact administrator for site setup to request charging space.",
                    show: true
                };
            }
        }
        return { show: false };
    }, [userFound, isActive, userSite, siteLoading]);
    

    return (
        <Container className="p-4 p-md-5 my-4 bg-light shadow-sm">

        {/* <ToastContainer position="top-right" autoClose={5000} hideProgressBar newestOnTop closeOnClick pauseOnHover theme="colored" /> */}

        {/* CONFIRM MODAL */}
        <ConfirmModal 
            show={showCancelModal}
            handleClose={() => setShowCancelModal(false)}
            handleConfirm={handleCancelConfirm}
            title="Confirm Cancellation"
            body="Are you sure you want to cancel this booking request? This action cannot be undone."
        />

        <ConfirmModal 
            show={showReleaseModal}
            handleClose={() => setShowReleaseModal(false)}
            handleConfirm={handleReleaseConfirm}
            title="Confirm Release Charging Space Booking"
            body="Are you sure you want to release this booked slot? This action cannot be undone."
        />
        
        {/* Page header */}
        <PageHeader
            title="EV Charging Request"
            description={`Request and request result for EV charging space.`}
            className="mb-3"
            ></PageHeader>
        
        {(loading || siteLoading) && (
        <div className="text-info d-flex align-items-center mb-3">
            <Spinner animation="border" size="sm" className="me-2" />
            {siteLoading ? 'Loading user settings...' : 'Updating Data...'}
        </div>
        )}

        {/* Display alert if user site/status is invalid */}
        {userAlert.show && (
            <Alert variant={userAlert.variant} className="mb-4 d-flex align-items-center">
                <i className="bi bi-exclamation-octagon-fill me-2"></i>
                <span dangerouslySetInnerHTML={{ __html: userAlert.message }} />
            </Alert>
        )}

        {/* Display alert if site is locked */}
        {isSiteLocked && (
            <Alert variant="info" className="mb-4 d-flex align-items-center">
                <i className="bi bi-lock-fill me-2"></i>
                Your charging site is fixed to **{currentSiteName}** by system administrator and cannot be changed.
            </Alert>
        )}

        {/* Card 1: Request Details */}
        <Card className="mb-4">
            <Card.Header as="h5" className="bg-white border-bottom">1. Your Request Details</Card.Header>
            <Card.Body>
                <Row className="g-3 align-items-end">
                    {/* Site picker (md=4) */}
                    <Col md={4}> 
                    <Form.Group>
                        <Form.Label className="text-muted small fw-bold">Charging Site</Form.Label>
                        <Form.Select 
                            value={site} 
                            onChange={(e) => setSite(e.target.value)} 
                            disabled={loading || siteLoading || isSiteLocked || !canUserRequest}
                        >
                        {SITES.map((s) => (
                            <option key={s.id} value={s.id}>
                            {s.name}
                            </option>
                        ))}
                        </Form.Select>
                    </Form.Group>
                    </Col>

                    {/* Slot type picker */}
                    <Col md={4}>
                    <Form.Group>
                        <Form.Label style={{marginTop:0}} className="text-muted required small fw-bold">Time slot</Form.Label>
                        <Form.Select value={slotType} onChange={(e) => setSlotType(e.target.value)} disabled={loading}>
                        {SLOT_TYPES.map((s) => (
                            <option key={s.id} value={s.id}>
                            {s.label}
                            </option>
                        ))}
                        </Form.Select>
                    </Form.Group>
                    </Col>

                    {/* Week selector */}
                    <Col md={4}>
                    <Form.Label className="text-muted small fw-bold d-block">Select Week</Form.Label>
                    <div className="d-flex align-items-center">
                        <Button variant="outline-secondary" size="sm" onClick={() => setAnchor(addDays(anchor, -7))} disabled={loading || !canGoBack}>
                        &lt;
                        </Button>
                        <span className="text-center flex-grow-1 mx-2 fw-medium">
                        {format(days[0], "EEE d MMM")} – {format(days[4], "EEE d MMM yyyy")}
                        </span>
                        <Button variant="outline-secondary" size="sm" onClick={() => setAnchor(addDays(anchor, 7))} disabled={loading || !canGoForward}>
                        &gt;
                        </Button>
                    </div>
                    </Col>
                </Row>
            </Card.Body>
        </Card>

        {/* Card 2: Day Picker */}
        <SpecificDaysPicker
            history = {history}
            userSite={userSite}
            displayedDays={displayedDays}
            userHistory={userHistory}
            isPastDay={isPastDay}
            loading={loading}
            getRemainingSlotCount={getRemainingSlotCount}
            renderAvailabilityBadge={renderAvailabilityBadge}
            submitOneDay={submitOneDay}
            canUserRequest={canUserRequest}
            WEEK_AHEAD_COUNT={WEEK_AHEAD_COUNT}
            MAX_WEKKLY_REQUEST={MAX_WEKKLY_REQUEST}
        />
        {/* Card 3: My recent requests */}
        <Card className="mb-4">
            <Card.Header as="h5" className="bg-white border-bottom">3. Your Recent Requests</Card.Header>
            <Card.Body>
                {userHistory.length === 0 && !loading ? (
                    <Alert variant="info" className="mb-0">You have no recent booking requests.</Alert>
                ) : (
                    userHistory.map(req => {
                        const bookingDate = new Date(req.bookingDate);
                        const isApprovedAndFuture = 
                            req.status === STATUS.APPROVED
                            && !isBefore(bookingDate.setHours(0, 0, 0, 0), today.getTime());

                        return (
                            <div 
                                key={req.id} 
                                className={`d-flex justify-content-between align-items-center p-3 mb-2 border rounded ${req.status === 'APPROVED' 
                                    ? 'bg-success-subtle border-success' : req.status === 'PENDING' 
                                    ? 'bg-warning-subtle border-warning' : req.status === 'RELEASED' 
                                    ? 'bg-info-subtle border-info' : 'bg-light-subtle border-secondary'}`}
                            >
                                <div>
                                    <div className="fw-bold">
                                        {req.bookingDate && format(new Date(req.bookingDate), 'EEE d MMM')} – ({req.slotType})
                                    </div>
                                    <small className="text-muted">
                                        Site: {req.site.replace("_", " ")} • Status: {renderStatusBadge(req.status)}
                                        {evUser?.plateNo && <span className="ms-2">• PlateNo: {evUser?.plateNo}</span>}
                                        {req.bayNo && <>• Bay: <Badge bg={'success'}>{ req.bayNo}</Badge></>} 
                                        {req.processedBy && <span className="ms-2">Processed by: {req.processedBy}</span>}
                                        {req.notes && <span className="ms-2 text-danger">({req.notes})</span>}
                                    </small>
                                </div>
                                
                                {/* Conditional Buttons */}
                                <div className="d-flex gap-2">
                                    {req.status === 'PENDING' && (
                                        <Button 
                                            size="sm" 
                                            variant="outline-secondary" 
                                            onClick={() => handleOpenCancelModal(req.id)}
                                            disabled={loading}
                                        >
                                            Cancel Request
                                        </Button>
                                    )}

                                    {isApprovedAndFuture && ( 
                                        <Button 
                                            size="sm" 
                                            variant="outline-info" 
                                            onClick={() => handleOpenReleaseModal(req.id)}
                                            disabled={loading}
                                        >
                                            Release
                                        </Button>
                                    )}

                                    {req.status === 'APPROVED' && !isApprovedAndFuture && (
                                        <Button size="sm" variant="success" disabled>Approved</Button>
                                    )}
                                    {req.status === 'REJECTED' && (
                                        <Button size="sm" variant="danger" disabled>Rejected</Button>
                                    )}
                                    {req.status === 'RELEASED' && (
                                        <Button size="sm" variant="info" disabled>Released</Button>
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}
            </Card.Body>
        </Card>

        {/* Footer */}
        <footer className="text-center text-muted small pt-3 border-top">
            Need help or urgent same-day charging? Please contact Facilities team immediately.
        </footer>
    </Container>
    );
}
